/**
 * TodaysAppointmentsPage — container.
 * Reads auth context, fetches today's appointments, buckets + maps them,
 * then renders TodaysAppointmentsView.
 */
import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Button } from '@scripsteam/scrips-signal-ds';
import { Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/auth/auth-store';
import { useSchedulingStore } from './store/scheduling-store';
import { useRole } from '@/auth/use-role';
import { useAppointments } from './services/use-appointments';
import { useAssignedProviders } from './services/use-assigned-providers';
import { useAppointmentMetaData } from './services/use-appointment-metadata';
import { usePractitionerPractices } from './services/use-practitioner-practices';
import { useDraftEncounters, draftAppointmentIds } from './services/use-draft-encounters';
import { bucketAppointments } from './lib/appointment-buckets';
import { buildPracticeOptions, resolveSelectedPracticeId } from './lib/practice-options';
import { visitEnded } from './lib/appointment-glyph';
import { toWorklistCard, type WorklistCardData } from './lib/to-worklist-card';
import { worklistProviderScope } from './lib/worklist-provider-scope';
import { toEncounterNav } from '@/encounter/lib/to-patient-header';
import { useIntelWarm } from '@/encounter/intelligence/use-intel-warm';
import { TodaysAppointmentsView } from './todays-appointments-view';
import type { BucketKey } from './todays-appointments-view';
import { useFrameV2 } from '@/shared/lib/use-frame-v2';
import { TodayV2 } from './today-v2/TodayV2';
import type { ListAppointmentItem } from '@/types/appointment-api';
import { PhoneCallDialog } from '@/dashboard/phone-call-dialog';
import { PhoneCallPanel } from '@/dashboard/phone-call-panel';
import { usePhoneCallDrawerStore } from '@/dashboard/store/phone-call-drawer-store';

const DEBOUNCE_MS = 300;

export interface TodaysAppointmentsPageProps {
  /** Today owns its page header: it carries the design's "Phone Call" secondary action (TodayScreen header) and mounts
   *  the same Phone Call dialog as the Dashboard. The Dashboard embeds this page under its own header and its own
   *  Phone Call, so it passes `false` (no second button, no second dialog). */
  phoneCall?: boolean;
  /** Embedded in /dashboard, whose top bar already carries the practice picker (same state): draw no second one. */
  hidePracticePicker?: boolean;
}

export function TodaysAppointmentsPage({ phoneCall = true, hidePracticePicker = false }: TodaysAppointmentsPageProps = {}) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  // The one Phone Call open state (the store AppShell reads to mark the rail inert), as on the Dashboard.
  const phoneCallOpen = usePhoneCallDrawerStore((s) => s.open);
  const setPhoneCallOpen = usePhoneCallDrawerStore((s) => s.setOpen);
  useEffect(() => {
    if (!phoneCall) return;
    return () => setPhoneCallOpen(false);
  }, [phoneCall, setPhoneCallOpen]);
  const practitionerId = user?.practitionerId ?? user?.id ?? user?.userId;
  const { isNurse } = useRole();
  const setActivePractice = useAuthStore((s) => s.setActivePractice);

  // Search state with 300 ms debounce.
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleSearch(value: string) {
    setSearchInput(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, DEBOUNCE_MS);
  }

  const [activeBucket, setActiveBucket] = useState<BucketKey>('upcoming');

  // Practice switcher — fetch the practitioner's practices and let the user pick.
  // Jack's token practiceId can be a different/empty practice; the selected
  // practiceId drives the appointment query.
  const { data: practicesData } = usePractitionerPractices(user?.id);

  // Fill logic — deduped + name-sorted (Flutter handlePracticeList). While the
  // list is loading/unavailable, fall back to the auth default so the switcher
  // still shows the login practice.
  const practiceOptions = useMemo(() => {
    const opts = buildPracticeOptions(practicesData);
    if (opts.length) return opts;
    // Fallback to the login practice while the list loads — but only when it has
    // a real id (never surface an empty-value option if both are unset).
    return user?.practiceId
      ? [{ label: user.practiceName ?? 'My Practice', value: user.practiceId }]
      : [];
  }, [practicesData, user?.practiceName, user?.practiceId]);

  // The selection IS the app-wide active practice (`user.practiceId`), not page-local state, so the page's picker and
  // the top-bar picker (`top-bar-practice.tsx`) are one state: a switch in either sticks (B5). A local copy seeded
  // once from the login practice is what used to undo a top-bar switch through the lock effect below.
  const setSchedulingPracticeId = useSchedulingStore((s) => s.setSelectedPracticeId);
  const handlePracticeChange = useCallback(
    (practiceId: string) => {
      setSchedulingPracticeId(practiceId);
      const p = practicesData?.find((x) => x.practiceSetupId === practiceId);
      setActivePractice({ practiceSetupId: practiceId, practiceName: p?.practiceName ?? null });
    },
    [practicesData, setActivePractice, setSchedulingPracticeId],
  );

  // Selection precedence (Flutter handlePracticeList): keep the current/login
  // practice if it's in the list, else the isPrimary practice, else the first.
  const resolvedPracticeId = useMemo(
    () => resolveSelectedPracticeId(practiceOptions, practicesData, user?.practiceId ?? ''),
    [practiceOptions, practicesData, user?.practiceId],
  );

  // Lock the app-wide active practice to the one the worklist is showing (PROD-1478).
  // The api-client's PracticeId header defaults to `user.practiceId` (the login practice), so
  // without this the ENTIRE encounter flow (CreateEncounter, GetEncounter, save-QR, vitals,
  // A&P, wrap-up…) would go out scoped to the LOGIN practice, not the practice whose
  // appointments you're viewing — CreateEncounter 400s "Encounter not exists" for a non-login
  // practice. Flutter does this globally (dio interceptor reads the persisted
  // `providerDefaultPracticeId`); PA's equivalent is `auth-store.setActivePractice` (PROD-1190,
  // which persists + re-keys the PracticeId header). The Calendar path syncs the same way (this
  // PR). Keeps the selected practice "locked in" as you move into the encounter.
  useEffect(() => {
    if (!resolvedPracticeId || resolvedPracticeId === user?.practiceId) return;
    const p = practicesData?.find((x) => x.practiceSetupId === resolvedPracticeId);
    setActivePractice({ practiceSetupId: resolvedPracticeId, practiceName: p?.practiceName ?? null });
  }, [resolvedPracticeId, user?.practiceId, practicesData, setActivePractice]);

  // PROD-2743 / A24 · the new-design Today screen, switch ON only (`?ef=frameV2`). Read once per mount.
  // OFF renders TodaysAppointmentsView exactly as before and mounts nothing new.
  const frameV2 = useFrameV2();

  // PROD-3045 N1 · whose visits to list, switch ON only for now (Samer, 6 Oct: merge before the design
  // stamp only if every change is invisible with the switch OFF; the OFF half ships in the follow-up
  // PR after the stamp). ON: a physician's own visits (filter id = doctorSetupId, user.id, matching
  // the Flutter request); a nurse's, the providers she supports, from /Staff/AssignedProviders for
  // this practice and today (Flutter appointment_list_widget.dart:126-133), so she can open intake
  // from Today. The rule and its two edge choices live in `lib/worklist-provider-scope.ts`.
  // OFF: exactly today's request, the signed-in user's own id, and no provider-list call.
  const nurseScopeOn = frameV2 && isNurse;
  const assignedParams = useMemo(
    () => (nurseScopeOn && resolvedPracticeId ? { practiceId: resolvedPracticeId } : null),
    [nurseScopeOn, resolvedPracticeId],
  );
  const assignedQuery = useAssignedProviders(assignedParams);
  const providerScope = useMemo(
    () =>
      frameV2
        ? worklistProviderScope({
            isNurse,
            selfId: user?.practitionerId ?? user?.id,
            assignedProviderIds: assignedQuery.data,
          })
        : // OFF, unchanged: confirmed filter id = doctorSetupId (user.id), matching the Flutter request.
          { practitionerId: user?.practitionerId ?? user?.id },
    [frameV2, isNurse, user?.practitionerId, user?.id, assignedQuery.data],
  );

  const queryParams = useMemo(() => {
    if (!user?.organizationId || !resolvedPracticeId || !providerScope) return null;
    return {
      organizationId: user.organizationId,
      practiceId: resolvedPracticeId,
      ...providerScope,
      timezone: user.timeZone,
      searchText: debouncedSearch,
    };
  }, [user?.organizationId, user?.timeZone, resolvedPracticeId, providerScope, debouncedSearch]);

  const { data, isLoading, isError, refetch } = useAppointments(queryParams);
  // The nurse's provider list is a FUNCTIONAL dependency of the list (no list → no request), so its
  // loading and failure join the page's gate: without it a nurse would read "No appointments today"
  // while the list is still on its way, and a failed read would look like an empty day.
  const scopeLoading = nurseScopeOn && assignedQuery.isLoading;
  const scopeError = nurseScopeOn && assignedQuery.isError;

  // Draft (in-progress) encounters for this provider — the In-Progress bucket key
  // (PROD-1300). The appointment LIST wire carries no encounterId, so bucket by a
  // match against this list (Flutter parity), not a per-row field. This is a
  // FUNCTIONAL dependency (it decides which appointments are In-Progress vs
  // Upcoming), so — unlike the cosmetic metaData query below — its loading/error
  // join the page's gate: otherwise a transient failure or the initial-load window
  // silently buckets every in-progress appointment back into Upcoming (Codex WARN).
  const draftQuery = useDraftEncounters(user?.practitionerId ?? user?.id);
  const draftApptIds = useMemo(() => draftAppointmentIds(draftQuery.data), [draftQuery.data]);

  const buckets = useMemo(
    () => bucketAppointments(data?.appointments ?? [], draftApptIds),
    [data, draftApptIds],
  );
  // Warm-from-Today (4 Nov): the next visit's Explain is warmed once, with this clinician's token; nothing is shown.
  // Only once the in-progress lookup has settled, and only the signed-in practitioner's own visits.
  useIntelWarm(data && draftQuery.isFetched ? buckets : null, user?.practitionerId ?? user?.id);

  // Per-appointment metadata (PROD-1280): the "Triaged" status override + the
  // vaccination-admin stripe. One batched POST for all visible appointment ids,
  // looked up per row by appointmentId (Flutter parity — no per-row N+1).
  const apptIds = useMemo(
    () => (data?.appointments ?? []).map((a) => a.id),
    [data],
  );
  const { data: metaMap } = useAppointmentMetaData(apptIds);

  const counts = {
    upcoming: buckets.upcoming.length,
    inProgress: buckets.inProgress.length,
    completed: buckets.completed.length,
  };

  const activeItems = useMemo((): WorklistCardData[] => {
    const inProgress = activeBucket === 'in-progress';
    const source = inProgress
      ? buckets.inProgress
      : activeBucket === 'upcoming'
        ? buckets.upcoming
        : buckets.completed;
    return source.map((item) =>
      toWorklistCard(item, { inProgress, ended: visitEnded(item, Date.now()), meta: metaMap?.get(item.id) }),
    );
  }, [activeBucket, buckets, metaMap]);

  function retry() {
    if (scopeError) void assignedQuery.refetch();
    // ON: without params the list query is disabled, and a forced refetch would run it with none.
    // OFF keeps today's unconditional refetch.
    if (!frameV2 || queryParams) void refetch();
    void draftQuery.refetch();
  }

  function handleOpenAppt(id: string) {
    const item = data?.appointments?.find((a) => a.id === id);
    const state = item ? toEncounterNav(item) : undefined;
    void navigate(`/encounter/${id}`, state ? { state } : undefined);
  }

  const cardFor = useCallback(
    (item: ListAppointmentItem, bucket: BucketKey) =>
      toWorklistCard(item, {
        inProgress: bucket === 'in-progress',
        ended: visitEnded(item, Date.now()),
        meta: metaMap?.get(item.id),
      }),
    [metaMap],
  );
  if (frameV2) {
    return (
      <TodayV2
        practices={practiceOptions}
        selectedPracticeId={resolvedPracticeId}
        onPracticeChange={handlePracticeChange}
        search={searchInput}
        onSearch={handleSearch}
        activeBucket={activeBucket}
        onBucketChange={setActiveBucket}
        counts={counts}
        buckets={buckets}
        cardFor={cardFor}
        loading={isLoading || draftQuery.isLoading || scopeLoading}
        error={isError || draftQuery.isError || scopeError}
        onRetry={retry}
        onOpenVisit={handleOpenAppt}
        hidePracticePicker={hidePracticePicker}
      />
    );
  }

  const view = (
    <TodaysAppointmentsView
      headerAction={
        phoneCall ? (
          <Button
            variant="secondary"
            size="md"
            icon={<Phone className="size-4" />}
            iconPosition="leading"
            onClick={() => setPhoneCallOpen(true)}
          >
            Phone Call
          </Button>
        ) : undefined
      }
      practices={practiceOptions}
      selectedPracticeId={resolvedPracticeId}
      onPracticeChange={handlePracticeChange}
      search={searchInput}
      onSearch={handleSearch}
      activeBucket={activeBucket}
      onBucketChange={setActiveBucket}
      counts={counts}
      appts={activeItems}
      loading={isLoading || draftQuery.isLoading || scopeLoading}
      error={isError || draftQuery.isError || scopeError}
      onRetry={retry}
      onOpenAppt={handleOpenAppt}
      hidePracticePicker={hidePracticePicker}
    />
  );
  if (!phoneCall) return view;
  return (
    <>
      {/* Everything but the dialog is inert while it is open (the rail too, through the store), as on the Dashboard. */}
      <div className="flex h-full min-h-0 flex-col" inert={phoneCallOpen}>
        {view}
      </div>
      <PhoneCallDialog open={phoneCallOpen} onClose={() => setPhoneCallOpen(false)}>
        {phoneCallOpen ? <PhoneCallPanel practitionerId={practitionerId} onClose={() => setPhoneCallOpen(false)} /> : null}
      </PhoneCallDialog>
    </>
  );
}
